import { ChevronRightIcon, CreditCard, TriangleAlert } from 'lucide-react';
import { useNavigate } from 'react-router';

import ErrorComponent from '@/components/errors/error';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import EmptyState from '@/components/ui/empty-state';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { useCustomerPortalSession } from '@/features/checkout/api/create-customer-portal-session';

import { useTeamSubscriptions } from '../../api/getTeamSubscription';

import { SettingsHeader } from './common-settings';

interface SubscriptionViewProps {
  teamId: string;
  isCaptain: boolean;
}

export function SubscriptionView({ teamId, isCaptain }: SubscriptionViewProps) {
  const {
    data: subscriptions,
    isLoading,
    error,
  } = useTeamSubscriptions({
    teamId,
    queryConfig: {
      refetchOnWindowFocus: 'always',
    },
  });
  const navigate = useNavigate();
  const portalMutation = useCustomerPortalSession({
    mutationConfig: {
      onSuccess: (data) => {
        setTimeout(() => {
          window.open(data.url, '_blank');
        });
      },
    },
  });

  if (error) {
    <ErrorComponent message={error.message} />;
  }

  return (
    <>
      <SettingsHeader
        title="Subscription"
        description={
          isCaptain
            ? 'View and manage your team’s subscription details.'
            : 'View your team’s subscription details.'
        }
      />
      <ScrollArea className="flex max-h-full flex-col px-6 py-4 pt-2">
        <div className="space-y-4">
          {isLoading ? (
            <Card className="flex flex-col bg-transparent p-4">
              <CardContent className="flex flex-row items-center justify-between gap-4 p-0">
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-24" />
                </div>
              </CardContent>
            </Card>
          ) : subscriptions && subscriptions.length > 0 ? (
            subscriptions.map((subscription) => (
              <Card
                key={subscription.id}
                className="flex flex-col bg-transparent p-4"
              >
                <CardContent className="flex flex-row items-center justify-between gap-4 p-0">
                  <div className="flex-1">
                    <p className="text-sm font-medium">Status</p>
                    <p className="capitalize text-muted-foreground">
                      {subscription.status}
                    </p>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Start Date</p>
                    <p className="text-muted-foreground">
                      {subscription.currentPeriodStart
                        ? new Date(
                            subscription.currentPeriodStart * 1000,
                          ).toLocaleDateString()
                        : 'N/A'}
                    </p>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">End Date</p>
                    <p className="text-muted-foreground">
                      {subscription.currentPeriodEnd
                        ? new Date(
                            subscription.currentPeriodEnd * 1000,
                          ).toLocaleDateString()
                        : 'N/A'}
                    </p>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Amount</p>
                    <p className="text-muted-foreground">
                      {subscription.amount
                        ? `$${(subscription.amount / 100).toFixed(2)}`
                        : 'N/A'}
                    </p>
                  </div>
                </CardContent>
                {subscription.status === 'canceled' && (
                  <p className="text-sm text-destructive">
                    This subscription has been canceled.
                  </p>
                )}
                {(subscription.status === 'past_due' ||
                  subscription.status === 'unpaid') && (
                  <p className="mt-2 flex items-center gap-x-1.5 text-xs text-destructive">
                    <TriangleAlert size={16} />
                    Payment failed. Please retry your payment in the billing portal to
                    continue using premium features.
                  </p>
                )}
                {subscription.status === 'active' &&
                  subscription.cancelAtPeriodEnd && (
                    <p className="flex items-center gap-x-1.5 text-sm text-yellow">
                      <TriangleAlert size={16} />
                      This subscription will not renew and ends on{' '}
                      {subscription.currentPeriodEnd
                        ? new Date(
                            subscription.currentPeriodEnd * 1000,
                          ).toLocaleDateString()
                        : 'N/A'}
                      .
                    </p>
                  )}
              </Card>
            ))
          ) : (
            <EmptyState
              title="No Active Subscription"
              description="This team currently has no active subscription."
              icon={<CreditCard className="h-6 w-6 text-muted-foreground" />}
              action={
                isCaptain ? (
                  <Button onClick={() => navigate('/onboarding')} size="sm">
                    Add Subscription
                  </Button>
                ) : undefined
              }
            />
          )}
          {isCaptain && subscriptions && subscriptions.length > 0 && (
            <Button
              className="group h-auto w-full justify-start gap-4 bg-background py-3 text-left"
              variant="outline"
              onClick={() => portalMutation.mutate(undefined)}
              disabled={portalMutation.isPending}
            >
              <Avatar className="size-10">
                <AvatarFallback>
                  <CreditCard className="h-6 w-6 text-primary" />
                </AvatarFallback>
              </Avatar>
              <div className="space-y-1">
                <h3>Manage Subscription</h3>
                <p className="whitespace-break-spaces font-normal text-muted-foreground">
                  Update billing details and more
                </p>
              </div>
              <ChevronRightIcon
                className="ml-auto opacity-60 transition-transform group-hover:translate-x-0.5"
                size={16}
                aria-hidden="true"
              />
            </Button>
          )}
        </div>
      </ScrollArea>
    </>
  );
}
